import type { ReactNode } from "react"

import { qrMatrix } from "./qr"
import styles from "./qr-code.module.css"

/**
 * QR-код как SVG.
 *
 * ПОЧЕМУ SVG, А НЕ CANVAS: картинка должна печататься и масштабироваться без мыла —
 * человек наводит на неё камеру с расстояния вытянутой руки, а иногда распечатывает,
 * чтобы завести фактор с другого телефона. И SVG видно в тестах разметкой, а не пикселями.
 *
 * ОДИН PATH НА ВСЕ ТЁМНЫЕ МОДУЛИ, а не тысяча <rect>: у версии 10 модулей больше трёх
 * тысяч, и каждый отдельным узлом — это заметная пауза при отрисовке на телефоне.
 *
 * ТИХАЯ ЗОНА ОБЯЗАТЕЛЬНА. Четыре модуля светлого поля по краю — требование стандарта,
 * без них часть приложений код не находит вовсе. Она делается полями viewBox, а не
 * отступом в CSS: отступ можно случайно снять чужим правилом, а поле внутри картинки —
 * часть самой картинки.
 */
const QUIET_ZONE = 4

export function QrCode(props: { readonly text: string; readonly label: string }): ReactNode {
  /*
   * ОШИБКА КОДИРОВЩИКА НЕ РОНЯЕТ ЭКРАН.
   *
   * qrMatrix отказывается от строки, которая не влезает в версию 10 (216 байт), — и это
   * верно: молча обрезанный хвост дал бы QR, ведущий в никуда. Но бросать исключение
   * ПРЯМО В ОТРИСОВКЕ нельзя: падает всё дерево, и человек видит пустой экран вместо
   * заведения фактора. А длинная ссылка отсюда достижима — подпись в аутентификаторе
   * содержит адрес учётки, и адрес бывает длинным.
   *
   * Картинки нет — остаётся ключ цифрами рядом: в аутентификатор его вводят руками,
   * и этим путём фактор заводится целиком.
   */
  let matrix: readonly (readonly boolean[])[]
  try {
    matrix = qrMatrix(props.text)
  } catch {
    return null
  }
  const size = matrix.length
  const side = size + QUIET_ZONE * 2

  let path = ""
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      if (matrix[row]?.[column] === true) {
        path += `M${String(column + QUIET_ZONE)} ${String(row + QUIET_ZONE)}h1v1h-1z`
      }
    }
  }

  return (
    <svg
      className={styles.qr}
      viewBox={`0 0 ${String(side)} ${String(side)}`}
      role="img"
      aria-label={props.label}
      // shapeRendering: без него браузер сглаживает края модулей, и между ними появляются
      // серые полуприкрытые пиксели — камера читает такой код хуже.
      shapeRendering="crispEdges"
    >
      <rect className={styles.plate} x="0" y="0" width={side} height={side} />
      <path className={styles.modules} d={path} />
    </svg>
  )
}
